import { SetupAssistant } from '__pages/Marketplace/components/SetupAssistant';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { useCallback, useMemo } from 'react';
import { processConfig } from '../usePluginEnable';
import { useMutation, useQuery } from '@tanstack/react-query';
import TeamMemberService from 'services/TeamMemberService';
import Loader from '__components/Kizen/Loader';
import { PLUGINS } from '__queries/query-keys';
import { useMarketplacePermissions } from '../../permissions';

type UserSettingsPanelProps = {
  plugin: MarketplacePlugin & {
    base_config: any;
  };
  businessPluginAppsByApiName: Record<string, any>;
  refetch: () => Promise<unknown>;
  businessPluginApp: Record<string, any>;
  canEditJSON: boolean;
};

export const UserSettingsPanel = (props: UserSettingsPanelProps) => {
  const { plugin, businessPluginAppsByApiName, refetch } = props;
  const { canEditIntegrationSecrets } = useMarketplacePermissions();

  const { data: pluginConfigData, isLoading } = useQuery({
    queryKey: PLUGINS.GET_USER_CONFIG(plugin.id),
    queryFn: () => TeamMemberService.getPluginConfig(plugin.id),
    enabled: Boolean(plugin.id),
  });

  const mutateConfig = useMutation({
    mutationFn: async (value: Record<string, any>) => {
      const res = await TeamMemberService.updatePluginConfig(plugin.id, {
        ...pluginConfigData,
        config: {
          ...pluginConfigData?.config,
          user_config: value,
        },
      });

      return res;
    },
    onSettled: () => {
      refetch();
    },
  });

  const modifiedPlugin = useMemo(() => {
    return {
      ...plugin,
      base_config: {
        ...plugin.base_config,
        setup_assistant: plugin.base_config?.user_setup_assistant,
      },
    };
  }, [plugin]);

  const modifiedBusinessPluginApps = useMemo(() => {
    return {
      ...businessPluginAppsByApiName,
      [plugin.api_name]: {
        ...businessPluginAppsByApiName[plugin.api_name],
        config: pluginConfigData?.config?.user_config || {},
      },
    };
  }, [businessPluginAppsByApiName, plugin.api_name, pluginConfigData]);

  const onChangeConfig = useCallback(
    async (pluginApiName: string, assistantConfig: any) => {
      const { newConfig } = await processConfig(
        pluginApiName,
        assistantConfig,
        modifiedBusinessPluginApps,
        canEditIntegrationSecrets,
        true
      );

      await mutateConfig.mutateAsync(newConfig);

      await refetch();
    },
    [modifiedBusinessPluginApps, mutateConfig, refetch, canEditIntegrationSecrets]
  );

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <>
      <SetupAssistant
        config={modifiedPlugin}
        onChangeConfig={onChangeConfig}
        businessPluginAppsByApiName={modifiedBusinessPluginApps}
        setupLevel="user"
        inline
      />
    </>
  );
};
